import {
  SALESFORCE_OAUTH_URL,
  HUBSPOT_OAUTH_URL,
  ZOHO_OAUTH_URL,
  PIPEDRIVE_OAUTH_URL,
  SLACK_OAUTH_URL
} from "Static/constant";
import { openConnectPopup } from "Pages/Dashboard/connectApps/oauthPopupBridge";
import { addLinkedInAccountPopupState } from "Components/PreviewPopup/AddLinkedInAccountPopup";
import { connectedEmailUtils } from "Pages/Dashboard/settings/ConnectedAccounts/ConnectedEmailUtils";
import { salesforceConnectedData } from "Pages/Dashboard/settings/Crm/salesforce/SalesforceConnectedData";
import salesforceIcon from "Assets/png/salesforce.png";
import hubspotIcon from "Assets/png/hubspot.png";
import zohoIcon from "Assets/png/zohoCrm.png";
import pipedriveIcon from "Assets/png/pipedriveIcon.png";
import slackIcon from "Assets/png/Slack.png";
import linkedinIcon from "Assets/svg/linkedin.svg";
import inboxIcon from "Assets/svg/email.svg";

/**
 * Provider registry for the in-chat connect card.
 *
 * Each entry reuses the SAME connect mechanism the settings page uses, so
 * connecting from the chat is the real flow, not a chat-only reimplementation:
 *   - LinkedIn drives `addLinkedInAccountPopupState`, the same global popup
 *     ConnectLinkedInButton opens. The popup is mounted at DashboardModule
 *     level, so it appears over /copilot without navigating anywhere — this is
 *     what makes "connect without leaving the chat" work.
 *   - The OAuth providers reuse `openConnectPopup` with the same URL + result
 *     `type` their respective *Configure page passes.
 *
 * `connect(onDone)` must call `onDone()` only on a real success; the block
 * reports that back to the agent so it can resume the blocked action.
 */
const oauthProvider = ({ label, icon, url, type, afterConnect }) => ({
  label,
  icon,
  isConfigured: () => !!url,
  connect: onDone => {
    openConnectPopup({
      url,
      name: `${label} Login`,
      types: [type],
      onResult: () => {
        if (afterConnect) afterConnect();
        onDone();
      },
      // Blocked or closed without connecting. Report it the same way LinkedIn
      // reports its multi-step handoff, so the card unlatches and the agent
      // re-checks rather than assuming success.
      onDismiss: () => onDone({ deferred: true })
    });
  }
});

export const CONNECT_PROVIDERS = {
  /*
   * An inbox is the one integration that stops a sequence from sending at all,
   * so it is also the connect prompt users hit most -- and it was the only one
   * with no card, leaving the copilot to hand out a link that navigates out of
   * the chat.
   *
   * Unlike the others it has more than one route in, so it declares `choices`.
   * Gmail and Outlook both drive flows reachable from anywhere (the Gmail
   * message popup is mounted at DashboardModule level; Outlook opens its own
   * OAuth window), so both run over the conversation. SMTP/IMAP is deliberately
   * absent: its chooser is defined inside the settings page and cannot be
   * driven from here, and a button that silently does nothing is worse than
   * not offering it.
   */
  inbox: {
    label: "Connect an inbox",
    icon: inboxIcon,
    isConfigured: () => true,
    choices: [
      {
        key: "gmail",
        label: "Gmail",
        connect: onDone => {
          connectedEmailUtils.openGmailConnectPopup();
          // Multi-step and owned by the popup, exactly like LinkedIn: report a
          // hand-off so the card unlatches without telling the agent an
          // outcome that has not happened.
          if (onDone) onDone({ deferred: true });
        }
      },
      {
        key: "outlook",
        label: "Outlook",
        connect: onDone => {
          connectedEmailUtils.microsoftAuth();
          if (onDone) onDone({ deferred: true });
        }
      }
    ],
    connect: onDone => {
      connectedEmailUtils.openGmailConnectPopup();
      if (onDone) onDone({ deferred: true });
    }
  },
  linkedin: {
    label: "LinkedIn",
    icon: linkedinIcon,
    isConfigured: () => true,
    // Opens the same popup as Settings → Connected Accounts, in place.
    connect: onDone => {
      addLinkedInAccountPopupState.setShowPopup(true);
      // The popup owns the multi-step connect (plugin check, profile pick), so
      // there is no single success callback to hook. The agent re-checks the
      // connection when the user comes back rather than assuming success.
      if (onDone) onDone({ deferred: true });
    }
  },
  salesforce: oauthProvider({
    label: "Salesforce",
    icon: salesforceIcon,
    url: SALESFORCE_OAUTH_URL,
    type: "SALESFORCE_CONNECT_OAUTH",
    afterConnect: () => salesforceConnectedData.getSalesForceOauthData()
  }),
  hubspot: oauthProvider({
    label: "HubSpot",
    icon: hubspotIcon,
    url: HUBSPOT_OAUTH_URL,
    type: "HUBSPOT_CONNECT_OAUTH"
  }),
  zoho: oauthProvider({
    label: "Zoho CRM",
    icon: zohoIcon,
    url: ZOHO_OAUTH_URL,
    type: "ZOHO_CONNECT_OAUTH"
  }),
  pipedrive: oauthProvider({
    label: "Pipedrive",
    icon: pipedriveIcon,
    url: PIPEDRIVE_OAUTH_URL,
    type: "PIPEDRIVE_CONNECT_OAUTH"
  }),
  slack: oauthProvider({
    label: "Slack",
    icon: slackIcon,
    url: SLACK_OAUTH_URL,
    type: "SLACK_CONNECT_OAUTH"
  })
};

export const getConnectProvider = provider =>
  CONNECT_PROVIDERS[String(provider || "").toLowerCase()] || null;

export default CONNECT_PROVIDERS;
